import React from 'react';
import UnknownProfileIcon from 'assets/images/StatusIndicator/UnknownProfile.svg';
import ConnectedProfileIcon from 'assets/images/StatusIndicator/ConnectedProfile.svg';
import DisconnectedProfileIcon from 'assets/images/StatusIndicator/DisconnectedProfile.svg';
import InactiveProfileIcon from 'assets/images/StatusIndicator/InactiveProfile.svg';
import AlertrSIM from 'assets/images/StatusIndicator/AlertrSIM.svg';

import {
  PollingSimStatus,
  RSimProfile,
  SimStatusIconProps,
} from './PollingSimStatusIcon.interface';

import { useTranslation } from 'react-i18next';
import { Tooltip } from 'common/components/Tooltip/Tooltip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { pxToRem } from 'src/common/utils/fonts';

type FCWithChildren = React.FC<React.PropsWithChildren>;
const IconAlertBox: FCWithChildren = ({ children }) => (
  <Box
    sx={{
      position: 'absolute',
      top: pxToRem(-3),
      right: pxToRem(-3),
    }}
  >
    {children}
  </Box>
);

const AlertIcon = () => (
  <IconAlertBox>
    <AlertrSIM height={pxToRem(13)} data-testid="Pause icon" />
  </IconAlertBox>
);

const IconRootBox: FCWithChildren = React.forwardRef(({ children, ...props }, ref) => (
  <Box ref={ref} sx={{ position: 'relative', width: pxToRem(24), height: pxToRem(24) }} {...props}>
    {children}
  </Box>
));

const getTooltipTitle = (profileLabel: string, statusLabel: string) => {
  return `${profileLabel} ${statusLabel.toLowerCase()}`;
};

export const PollingSimStatusIcon: React.FC<SimStatusIconProps> = ({
  status,
  paused = false,
  profile,
  hideTooltip,
}) => {
  const { t } = useTranslation();
  const alertDecriptionRows = paused ? `${t('common.simulateOutageRunning')}!` : [];
  const profileName = profile === RSimProfile.Primary ? t('common.primary') : t('common.backup');
  let statusLabel = t('common.unknown');

  if (!hideTooltip) {
    if (status === PollingSimStatus.Active) {
      statusLabel = t('common.connected');
      return (
        <Tooltip
          title={getTooltipTitle(profileName, statusLabel)}
          description={alertDecriptionRows}
        >
          <IconRootBox>
            <ConnectedProfileIcon
              width={pxToRem(24)}
              height={pxToRem(24)}
              aria-label={statusLabel}
              role="img"
            />
            {paused && <AlertIcon />}
          </IconRootBox>
        </Tooltip>
      );
    }

    if (status === PollingSimStatus.Inactive) {
      statusLabel = t('common.inactive');
      return (
        <Tooltip
          title={getTooltipTitle(profileName, statusLabel)}
          description={alertDecriptionRows}
        >
          <IconRootBox>
            <InactiveProfileIcon
              width={pxToRem(24)}
              height={pxToRem(24)}
              aria-label={statusLabel}
              role="img"
            />
            {paused && <AlertIcon />}
          </IconRootBox>
        </Tooltip>
      );
    }

    if (status === PollingSimStatus.Disconnected) {
      statusLabel = t('common.disconnected');
      return (
        <Tooltip
          title={getTooltipTitle(profileName, statusLabel)}
          description={alertDecriptionRows}
        >
          <IconRootBox>
            <DisconnectedProfileIcon
              width={pxToRem(24)}
              height={pxToRem(24)}
              aria-label={statusLabel}
              role="img"
            />
            {paused && <AlertIcon />}
          </IconRootBox>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={statusLabel} description={alertDecriptionRows}>
        <IconRootBox>
          <UnknownProfileIcon
            width={pxToRem(24)}
            height={pxToRem(24)}
            aria-label={statusLabel}
            role="img"
          />
          {paused && <AlertIcon />}
        </IconRootBox>
      </Tooltip>
    );
  }

  if (status === PollingSimStatus.Active) {
    statusLabel = t('common.connected');
    return (
      <Box sx={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <IconRootBox>
          <ConnectedProfileIcon
            width={pxToRem(24)}
            height={pxToRem(24)}
            aria-label={statusLabel}
            role="img"
          />
          {paused && <AlertIcon />}
        </IconRootBox>
        <Typography variant="text1" color="text.primary">
          {statusLabel}
        </Typography>
      </Box>
    );
  }

  if (status === PollingSimStatus.Inactive) {
    statusLabel = t('common.inactive');
    return (
      <Box sx={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <IconRootBox>
          <InactiveProfileIcon
            width={pxToRem(24)}
            height={pxToRem(24)}
            aria-label={statusLabel}
            role="img"
          />
          {paused && <AlertIcon />}
        </IconRootBox>
        <Typography variant="text1" color="text.primary">
          {statusLabel}
        </Typography>
      </Box>
    );
  }

  if (status === PollingSimStatus.Disconnected) {
    statusLabel = t('common.disconnected');
    return (
      <Box sx={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <IconRootBox>
          <DisconnectedProfileIcon
            width={pxToRem(24)}
            height={pxToRem(24)}
            aria-label={statusLabel}
            role="img"
          />
          {paused && <AlertIcon />}
        </IconRootBox>
        <Typography variant="text1" color="text.primary">
          {statusLabel}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <IconRootBox>
        <UnknownProfileIcon
          width={pxToRem(24)}
          height={pxToRem(24)}
          aria-label={statusLabel}
          role="img"
        />
        {paused && <AlertIcon />}
      </IconRootBox>
      <Typography variant="text1" color="text.primary">
        {statusLabel}
      </Typography>
    </Box>
  );
};
